import { createContext, useCallback, useContext, useRef, useState } from 'react';

const InteractionsListContext = createContext(null);

export const InteractionsListProvider = ({ children }) => {
  const [interactionsListCache, setInteractionsListCache] = useState(null);
  // A ref, not state: saving it happens in a Link's onClick right before the click's own
  // navigation unmounts the list, so it has to take effect synchronously - a setState here would
  // depend on that update committing before unmount, which isn't guaranteed.
  const scrollPositionRef = useRef(null);
  const [showLogInteractionModal, setShowLogInteractionModal] = useState(false);

  // For pages that can't reach the list's own live `interactions` state directly (e.g. the
  // interaction detail page - a separate route, unmounted while it's open). Since the list always
  // remounts and re-seeds from this cache on navigating back (see useCrmInteractionsQuery's
  // cachedResult handling), patching the cache here is enough for the edit to show up with no refetch.
  const updateInteractionInCache = useCallback((updatedInteraction) => {
    setInteractionsListCache((prev) =>
      prev?.hits
        ? {
            ...prev,
            hits: prev.hits.map((item) =>
              item.id === updatedInteraction.id ? { ...item, ...updatedInteraction } : item
            ),
          }
        : prev
    );
  }, []);

  return (
    <InteractionsListContext.Provider
      value={{
        interactionsListCache,
        setInteractionsListCache,
        updateInteractionInCache,
        scrollPositionRef,
        showLogInteractionModal,
        setShowLogInteractionModal,
      }}
    >
      {children}
    </InteractionsListContext.Provider>
  );
};

export function useInteractionsList() {
  const context = useContext(InteractionsListContext);
  if (context == null) {
    throw new Error('useInteractionsList must be used within an InteractionsListProvider.');
  }
  return context;
}

export default InteractionsListContext;
